import * as React from 'react';
import Divider from '@mui/material/Divider';
import Drawer, { DrawerProps } from '@mui/material/Drawer';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

import styles from './layout.module.css';
import { Link, useLocation } from 'react-router-dom';
import { getNavigationCategory } from './utils';
import { getRequest } from 'api';

interface NavigationChild {
  id: string;
  icon: JSX.Element;
  path: string;
}

interface NavigationCategory {
  id: string;
  children: NavigationChild[];
}

export interface NavigatorProps extends DrawerProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const GROUPS_STORAGE_KEY = 'vschool_nav_groups';

export default function Navigator(props: NavigatorProps) {
  const { collapsed = false, onToggleCollapse, ...other } = props;
  const location = useLocation();
  const [navPath, setNavPath] = React.useState('');
  const [categories, setCategories] = React.useState<NavigationCategory[]>([]);
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(GROUPS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  React.useEffect(() => {
    setNavPath(location.pathname.split('/')[1] || '');
  }, [location.pathname]);

  React.useEffect(() => {
    const fetchMenuConfig = async () => {
      const role = localStorage.getItem('role') as string;
      const schoolId = localStorage.getItem('schoolId') || 1;

      try {
        const res = await getRequest(`/cms/menu-config?schoolId=${schoolId}`);
        let disabledPaths: string[] = [];
        if (res?.info) {
          const configObj = JSON.parse(res.info);
          disabledPaths = configObj[role] || [];
        }
        setCategories(getNavigationCategory(role, disabledPaths));
      } catch (error) {
        setCategories(getNavigationCategory(role, []));
      }
    };
    fetchMenuConfig();
  }, []);

  // Đảm bảo nhóm chứa trang hiện tại luôn tự mở
  React.useEffect(() => {
    if (!categories.length) return;
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      categories.forEach((cat) => {
        if (next[cat.id] === undefined) {
          next[cat.id] = true;
          changed = true;
        }
        const hasActiveChild = cat.children.some((c) => c.path === navPath);
        if (hasActiveChild && !next[cat.id]) {
          next[cat.id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [categories, navPath]);

  const handleToggleGroup = (groupId: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [groupId]: !(prev[groupId] ?? true) };
      try {
        localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore storage error
      }
      return next;
    });
  };

  const isHomeSelected = navPath === '';

  return (
    <Drawer variant="permanent" {...other}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowX: 'hidden',
          bgcolor: '#FFFFFF',
        }}
      >
        {/* Header Logo + Nút Thu/Mở nhanh */}
        <Box
          onClick={collapsed ? onToggleCollapse : undefined}
          sx={{
            minHeight: 46,
            height: 46,
            px: collapsed ? 0 : 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            flexShrink: 0,
            cursor: collapsed ? 'pointer' : 'default',
            '&:hover .expand-hint-icon': {
              opacity: 1,
            },
            '&:hover .collapsed-logo': {
              opacity: collapsed ? 0.15 : 1,
            },
            position: 'relative',
          }}
        >
          <Tooltip title={collapsed ? 'Mở rộng menu' : ''} placement="right" arrow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                overflow: 'hidden',
              }}
            >
              <img
                className="collapsed-logo"
                src="/Images/logo.png"
                alt="Vschool"
                style={{
                  width: 30,
                  height: 30,
                  objectFit: 'contain',
                  flexShrink: 0,
                  transition: 'opacity 0.2s ease',
                }}
              />
              {collapsed && (
                <KeyboardDoubleArrowRightIcon
                  className="expand-hint-icon"
                  sx={{
                    position: 'absolute',
                    fontSize: 20,
                    color: '#B9392E',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                  }}
                />
              )}
              {!collapsed && (
                <Typography
                  sx={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: '#000000',
                    letterSpacing: 0.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Vschool
                </Typography>
              )}
            </Box>
          </Tooltip>

          {!collapsed && onToggleCollapse && (
            <Tooltip title="Thu gọn menu" placement="right" arrow>
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                sx={{
                  color: '#000000',
                  p: 0.5,
                  '&:hover': { color: '#B9392E', bgcolor: 'rgba(185, 57, 46, 0.06)' },
                }}
              >
                <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Danh sách điều hướng có cuộn mượt */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            py: 0.75,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.12)',
              borderRadius: '4px',
            },
          }}
        >
          <List disablePadding>
            {/* Trang chủ */}
            <Link to="/" className={styles.link}>
              <ListItem disablePadding sx={{ display: 'block' }}>
                <Tooltip title={collapsed ? 'Trang chủ' : ''} placement="right" arrow>
                  <ListItemButton
                    selected={isHomeSelected}
                    sx={{
                      minHeight: 36,
                      py: 0.6,
                      px: collapsed ? 0 : 1.5,
                      mx: 0.75,
                      mb: 0.5,
                      borderRadius: '6px',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      color: isHomeSelected ? '#B9392E' : '#000000',
                      position: 'relative',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(185, 57, 46, 0.09)',
                        color: '#B9392E',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '20%',
                          height: '60%',
                          width: '3px',
                          bgcolor: '#B9392E',
                          borderRadius: '0 3px 3px 0',
                        },
                      },
                      '&:hover': {
                        bgcolor: isHomeSelected
                          ? 'rgba(185, 57, 46, 0.12)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: collapsed ? 0 : 1.25,
                        justifyContent: 'center',
                        color: 'inherit',
                        '& svg': { fontSize: 19 },
                      }}
                    >
                      <HomeOutlinedIcon />
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary="Trang chủ"
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: isHomeSelected ? 600 : 500,
                          noWrap: true,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            </Link>

            <Divider sx={{ my: 0.5, mx: 1.5, borderColor: 'rgba(0,0,0,0.06)' }} />

            {/* Các nhóm danh mục */}
            {categories
              .filter((cat) => cat.children.length > 0)
              .map(({ id, children }, groupIdx) => {
                const isGroupOpen = openGroups[id] ?? true;
                const hasActiveChild = children.some((c) => c.path === navPath);

                return (
                  <Box key={id} sx={{ mb: 0.25 }}>
                    {collapsed ? (
                      groupIdx > 0 && (
                        <Divider sx={{ my: 0.5, mx: 1.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                      )
                    ) : (
                      <ListItemButton
                        onClick={() => handleToggleGroup(id)}
                        sx={{
                          py: 0.4,
                          px: 1.5,
                          mx: 0.75,
                          mt: 0.5,
                          minHeight: 26,
                          borderRadius: '5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.03)',
                          },
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            color: hasActiveChild ? '#B9392E' : '#000000',
                            lineHeight: 1.3,
                          }}
                        >
                          {id}
                        </Typography>
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 15,
                            color: hasActiveChild ? '#B9392E' : '#000000',
                            transform: isGroupOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </ListItemButton>
                    )}

                    <Collapse in={collapsed ? true : isGroupOpen} timeout="auto">
                      <List disablePadding sx={{ mt: 0.25 }}>
                        {children.map(({ id: childId, icon, path }: NavigationChild) => {
                          const isSelected = path === navPath;
                          return (
                            <Link to={`/${path}`} className={styles.link} key={childId}>
                              <ListItem disablePadding sx={{ display: 'block' }}>
                                <Tooltip
                                  title={collapsed ? `${childId} (${id})` : childId}
                                  placement="right"
                                  arrow
                                  disableHoverListener={!collapsed && childId.length < 24}
                                >
                                  <ListItemButton
                                    selected={isSelected}
                                    sx={{
                                      minHeight: 34,
                                      py: 0.5,
                                      px: collapsed ? 0 : 1.5,
                                      mx: 0.75,
                                      my: '1px',
                                      borderRadius: '6px',
                                      justifyContent: collapsed ? 'center' : 'flex-start',
                                      color: isSelected ? '#B9392E' : '#000000',
                                      position: 'relative',
                                      '&.Mui-selected': {
                                        bgcolor: 'rgba(185, 57, 46, 0.09)',
                                        color: '#B9392E',
                                        '&::before': {
                                          content: '""',
                                          position: 'absolute',
                                          left: 0,
                                          top: '20%',
                                          height: '60%',
                                          width: '3px',
                                          bgcolor: '#B9392E',
                                          borderRadius: '0 3px 3px 0',
                                        },
                                      },
                                      '&:hover': {
                                        bgcolor: isSelected
                                          ? 'rgba(185, 57, 46, 0.13)'
                                          : 'rgba(0, 0, 0, 0.04)',
                                      },
                                    }}
                                  >
                                    <ListItemIcon
                                      sx={{
                                        minWidth: 0,
                                        mr: collapsed ? 0 : 1.25,
                                        justifyContent: 'center',
                                        color: 'inherit',
                                        '& svg': { fontSize: 18 },
                                      }}
                                    >
                                      {icon}
                                    </ListItemIcon>
                                    {!collapsed && (
                                      <ListItemText
                                        primary={childId}
                                        primaryTypographyProps={{
                                          fontSize: 13,
                                          fontWeight: isSelected ? 600 : 500,
                                          noWrap: true,
                                        }}
                                      />
                                    )}
                                  </ListItemButton>
                                </Tooltip>
                              </ListItem>
                            </Link>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
          </List>
        </Box>

        {/* Chân Sidebar: nút mở rộng nhanh khi đang ở chế độ thu gọn */}
        {onToggleCollapse && collapsed && (
          <Box
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              py: 0.75,
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Tooltip title="Mở rộng menu" placement="right" arrow>
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                sx={{
                  color: '#000000',
                  '&:hover': { color: '#B9392E', bgcolor: 'rgba(185, 57, 46, 0.06)' },
                }}
              >
                <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
